import React from 'react';
import { PiChartBar } from 'react-icons/pi';
import { ButtonSkeleton } from 'src/modules/shared/components/atoms/buttons/button-skeleton';
import { InputFieldSkeleton } from 'src/modules/shared/components/atoms/inputs/input-field-skeleton';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';

type ReportsSkeletonTab = 'basic' | 'advanced';

const ReportsHeaderSkeleton: React.FC = () => (
  <PanelHeader className="border-b px-4 py-3">
    <div className="flex items-center gap-4">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-neutral-100 text-neutral-300">
        <PiChartBar className="h-5 w-5" />
      </div>
      <div className="flex-1 space-y-2">
        <TextSkeleton className="h-8 w-40" />
        <TextSkeleton className="h-4 w-[360px] max-w-full" />
      </div>
    </div>
  </PanelHeader>
);

const ReportsSidebarSkeleton: React.FC = () => (
  <>
    <div className="hidden w-72 flex-shrink-0 border-r lg:block">
      <Panel variant="neutral" appearance="ghost" className="space-y-3 p-3">
        <ButtonSkeleton size="md" variant="secondary" fullWidth />
        <ButtonSkeleton size="md" variant="secondary" fullWidth />
      </Panel>
    </div>

    <div className="m-4 rounded-lg border border-neutral-200 bg-white p-4 lg:hidden">
      <TextSkeleton className="h-5 w-20" />
    </div>
  </>
);

export const ReportsFiltersSkeleton: React.FC = () => (
  <div className="flex w-full min-w-0 flex-col gap-3 md:flex-row md:items-end md:justify-between">
    <InputFieldSkeleton
      className="w-full md:w-[220px]"
      inputClassName="h-7 w-full"
    />
    <InputFieldSkeleton
      className="w-full md:w-[320px]"
      inputClassName="h-7 w-full"
    />
  </div>
);

const ReportCardSkeleton: React.FC<{
  contentHeightClassName?: string;
}> = ({ contentHeightClassName = 'h-[320px]' }) => (
  <section className="min-w-0 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
    <div className="border-b border-neutral-100 px-5 py-4">
      <TextSkeleton className="h-6 w-44" />
      <TextSkeleton className="mt-2 h-4 w-[340px] max-w-full" />
    </div>
    <div className="space-y-4 px-4 py-4">
      <div
        className={`w-full animate-pulse rounded-xl bg-neutral-100 ${contentHeightClassName}`}
      />
    </div>
  </section>
);

export const BasicReportsSkeleton: React.FC = () => (
  <div className="grid gap-4 xl:grid-cols-2">
    {Array.from({ length: 6 }).map((_, index) => (
      <ReportCardSkeleton key={`basic-report-skeleton-${index}`} />
    ))}
  </div>
);

export const AdvancedReportsSkeleton: React.FC = () => (
  <div className="space-y-6 overflow-x-hidden px-1 pb-4">
    <section className="min-w-0 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-100 px-5 py-4">
        <TextSkeleton className="h-6 w-48" />
        <TextSkeleton className="mt-2 h-4 w-[380px] max-w-full" />
      </div>
      <div className="border-b border-neutral-100 bg-neutral-50 px-4 py-4">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <ButtonSkeleton
                key={`advanced-preset-skeleton-${index}`}
                size="xs"
                variant="secondary"
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <InputFieldSkeleton inputClassName="h-8 w-[176px]" />
            <InputFieldSkeleton inputClassName="h-8 w-[176px]" />
            <InputFieldSkeleton inputClassName="h-8 w-[220px]" />
          </div>
        </div>
      </div>
      <div className="px-4 py-4">
        <div className="w-full animate-pulse rounded-xl bg-neutral-100 h-[360px]" />
      </div>
    </section>

    <div className="grid gap-6 xl:grid-cols-2">
      <ReportCardSkeleton />
      <ReportCardSkeleton />
    </div>

    <ReportCardSkeleton contentHeightClassName="h-[280px]" />
  </div>
);

export const ReportsViewSkeleton: React.FC<{
  activeTab?: ReportsSkeletonTab;
}> = ({ activeTab = 'basic' }) => (
  <div className="flex h-full flex-col">
    <Panel className="flex h-full flex-col">
      <ReportsHeaderSkeleton />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <ReportsSidebarSkeleton />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-neutral-50">
          {activeTab === 'basic' ? (
            <>
              <PageFiltersBar>
                <ReportsFiltersSkeleton />
              </PageFiltersBar>
              <div className="min-w-0 flex-1 overflow-auto p-4">
                <BasicReportsSkeleton />
              </div>
            </>
          ) : (
            <div className="min-w-0 flex-1 overflow-auto p-4">
              <AdvancedReportsSkeleton />
            </div>
          )}
        </div>
      </div>
    </Panel>
  </div>
);
